import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartLine, TrendUp, Trophy, BookOpen, SignOut, Wallet,
  ChatCircleDots, GameController, Newspaper, GearSix, UsersThree,
  Coins, List, X,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from './ui/button';
import GuestBanner from './GuestBanner';
import CurrencyPicker from './CurrencyPicker';
import LogoDropdown from './LogoDropdown';
import NotificationBell from './NotificationBell';

const NAV_ITEMS = [
  { path: '/dashboard',   label: 'Dashboard',   icon: ChartLine },
  { path: '/market',      label: 'Market',      icon: TrendUp },
  { path: '/portfolio',   label: 'Portfolio',   icon: Wallet },
  { path: '/game-modes',  label: 'Game Modes',  icon: GameController },
  { path: '/learn',       label: 'Learn',       icon: BookOpen },
  { path: '/news',        label: 'News',        icon: Newspaper },
  { path: '/community',   label: 'Community',   icon: UsersThree },
  { path: '/bets',        label: 'Bets',        icon: Coins },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  { path: '/help',        label: 'Help Buddy',  icon: ChatCircleDots },
];

/**
 * Full navbar rewrite: fixed vertical sidebar on desktop (lg+),
 * mobile top bar + slide-out drawer under lg. All pages get the
 * right padding automatically via the `has-sidebar` body class.
 */
const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { format: fmtCcy } = useCurrency();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Add body class so global CSS can shift page content right on lg+.
  useEffect(() => {
    document.body.classList.add('has-sidebar');
    return () => document.body.classList.remove('has-sidebar');
  }, []);

  // Close mobile drawer when route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <GuestBanner />

      {/* =================== DESKTOP SIDEBAR (lg+) =================== */}
      <motion.aside
        initial={{ x: -40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 z-40 glass border-r border-white/10 flex-col"
        data-testid="sidebar-desktop"
      >
        <div className="p-4 border-b border-white/5 flex items-center justify-between gap-2">
          <LogoDropdown />
          <NotificationBell align="left" />
        </div>
        <NavList pathname={location.pathname} />
        <Footer user={user} logout={logout} fmtCcy={fmtCcy} pathname={location.pathname} />
      </motion.aside>

      {/* =================== MOBILE TOP BAR (< lg) =================== */}
      <div className="lg:hidden sticky top-0 z-40 glass border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <LogoDropdown compact />
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/5" data-testid="user-balance-mobile">
              <Wallet size={14} className="text-gold" weight="duotone" />
              <span className="font-mono font-bold text-gold text-xs">{fmtCcy(user?.balance || 0)}</span>
            </div>
            <NotificationBell align="right" />
            <button
              onClick={() => setMobileOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white"
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <List size={20} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* =================== MOBILE DRAWER (< lg) =================== */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            />
            <motion.aside
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-72 z-50 bg-zinc-950 border-r border-white/10 flex flex-col"
              data-testid="sidebar-mobile-drawer"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <LogoDropdown compact />
                <button
                  onClick={() => setMobileOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-zinc-400 hover:text-white"
                  data-testid="mobile-menu-close"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
              <NavList pathname={location.pathname} onClick={() => setMobileOpen(false)} />
              <Footer user={user} logout={logout} fmtCcy={fmtCcy} pathname={location.pathname} onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// ---- Extracted sub-components (kept outside Navbar to avoid remounts) ----

const NavList = ({ pathname, onClick }) => (
  <nav className="flex-1 overflow-y-auto py-4 space-y-0.5">
    {NAV_ITEMS.map((item) => {
      const Icon = item.icon;
      const isActive = pathname === item.path;
      return (
        <Link
          key={item.path}
          to={item.path}
          onClick={onClick}
          className={`group flex items-center gap-3 mx-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
            isActive
              ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-bold shadow-[0_4px_20px_-6px_rgba(212,175,55,0.5)]'
              : 'text-zinc-400 hover:text-white hover:bg-white/5'
          }`}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
          <span>{item.label}</span>
        </Link>
      );
    })}
  </nav>
);

const Footer = ({ user, logout, fmtCcy, pathname, onNavigate }) => (
  <div className="border-t border-white/5 p-3 space-y-2">
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/5" data-testid="user-balance">
      <Wallet size={18} className="text-gold flex-shrink-0" weight="duotone" />
      <span className="font-mono font-bold text-gold text-sm truncate" data-testid="navbar-balance">
        {fmtCcy(user?.balance || 0)}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <div className="flex-1"><CurrencyPicker /></div>
      <Link
        to="/settings"
        onClick={onNavigate}
        className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
          pathname === '/settings'
            ? 'bg-gold/20 text-gold'
            : 'text-zinc-400 hover:text-white hover:bg-white/5'
        }`}
        data-testid="nav-settings"
        aria-label="Settings"
      >
        <GearSix size={18} weight={pathname === '/settings' ? 'fill' : 'regular'} />
      </Link>
      <button
        onClick={logout}
        className="w-9 h-9 flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        data-testid="logout-button"
        aria-label="Log out"
      >
        <SignOut size={18} />
      </button>
    </div>
    {user?.username && (
      <p className="text-[10px] text-zinc-500 px-2 truncate">
        Logged in as <span className="text-zinc-300">{user.username}</span>
      </p>
    )}
  </div>
);

export default Navbar;
